import { Dialog, Transition } from '@headlessui/react'
import { useFormik } from 'formik'
import { Fragment, SyntheticEvent, useState } from 'react'
import * as Yup from 'yup'
import { toast } from 'react-hot-toast'

import { useChangePasswordMutation } from '../../../features/account/passwordSlice'
import { SuccessIcon } from '../../../shared/SuccessIcon'

export const ChangePassword = () => {
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)

  const [changePassword, { isLoading }] = useChangePasswordMutation()

  const formik = useFormik({
    initialValues: {
      oldPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    },
    validationSchema: Yup.object().shape({
      oldPassword: Yup.string().required('Provide your old password'),
      newPassword: Yup.string().required('Please provide a new password'),
      confirmNewPassword: Yup.string()
        .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
        .required('Please confirm your new password'),
    }),
    onSubmit: async (values) => {
      try {
        toast.loading('Trying to change password...')
        await changePassword({
          oldPassword: values.oldPassword,
          newPassword: values.newPassword,
        }).unwrap()
        formik.resetForm()

        toast.dismiss()
        setIsSuccessModalOpen(true)
      } catch (error: any) {
        console.log(error?.data?.message)
        toast.dismiss()
        toast.error(error?.data?.message)
      }
    },
  })

  return (
    <div className='w-full max-w-lg mx-auto md:py-12 px-4'>
      <form action='' onSubmit={formik.handleSubmit}>
        <div className='flex flex-col gap-2 mt-8'>
          <label htmlFor='oldPassword' className='text-xs text-white/70'>
            Old Password
          </label>
          <input
            id='oldPassword'
            name='oldPassword'
            type='text'
            value={formik.values.oldPassword}
            onChange={formik.handleChange}
            placeholder='••••••'
            className='form-input'
          />
          <p className='text-red-500 text-xs'>
            {formik.touched.oldPassword &&
              Boolean(formik.errors.oldPassword) &&
              formik.errors.oldPassword}
          </p>
        </div>
        <div className='flex flex-col gap-2 mt-4'>
          <label htmlFor='newPassword' className='text-xs text-white/70'>
            New Password
          </label>
          <input
            id='newPassword'
            name='newPassword'
            type='text'
            value={formik.values.newPassword}
            onChange={formik.handleChange}
            placeholder='••••••'
            className='form-input'
          />
          <p className='text-red-500 text-xs'>
            {formik.touched.newPassword &&
              Boolean(formik.errors.newPassword) &&
              formik.errors.newPassword}
          </p>
        </div>
        <div className='flex flex-col gap-2 mt-4'>
          <label htmlFor='confirmNewPassword' className='text-xs text-white/70'>
            Confirm New Password
          </label>
          <input
            id='confirmNewPassword'
            name='confirmNewPassword'
            type='text'
            value={formik.values.confirmNewPassword}
            onChange={formik.handleChange}
            placeholder='••••••'
            className='form-input'
          />
          <p className='text-red-500 text-xs'>
            {formik.touched.confirmNewPassword &&
              Boolean(formik.errors.confirmNewPassword) &&
              formik.errors.confirmNewPassword}
          </p>
        </div>

        <button
          type='submit'
          className='btn-primary px-4 py-2 rounded-full w-full text-base mx-auto mt-8'
        >
          Change
        </button>

        <Transition appear show={isSuccessModalOpen} as={Fragment}>
          <Dialog
            as='div'
            className='relative z-50'
            onClose={() => setIsSuccessModalOpen(false)}
          >
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0'
              enterTo='opacity-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100'
              leaveTo='opacity-0'
            >
              <div className='fixed inset-0 bg-black/40 backdrop-blur-sm' />
            </Transition.Child>

            <div className='fixed inset-0 overflow-y-auto'>
              <div className='flex min-h-full items-center justify-center p-4 text-center'>
                <Transition.Child
                  as={Fragment}
                  enter='ease-out duration-300'
                  enterFrom='opacity-0 scale-95'
                  enterTo='opacity-100 scale-100'
                  leave='ease-in duration-200'
                  leaveFrom='opacity-100 scale-100'
                  leaveTo='opacity-0 scale-95'
                >
                  <Dialog.Panel className='relative font-dashboard w-full max-w-3xl min-h-[500px] transform overflow-hidden rounded-2xl bg-[#161819] px-4 pt-4 pb-10 text-left align-middle shadow-xl transition-all'>
                    <div className='relative py-24 flex flex-col items-center'>
                      <div className='flex flex-col items-center gap-4'>
                        <SuccessIcon />
                        <p className='text-xl font-light text-white/40'>
                          Password is changed
                        </p>
                      </div>

                      <button
                        className='btn-primary py-2 rounded-lg w-full max-w-md mt-12'
                        onClick={() => {
                          setIsSuccessModalOpen(false)
                        }}
                      >
                        Done
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </form>
    </div>
  )
}
